/**
 * A friend invite (`/f/<token>`) opened while signed out. The token waits on
 * this device while the visitor signs up or signs in, and `PendingInviteReturn`
 * (App.tsx) sends them back to the invite once the session is proven. That
 * covers the Google round trip and an email-confirm link opened in a new tab,
 * both of which land on /app. localStorage, kept at most an hour (and cleared
 * when the visitor closes the sign-in sidebar still signed out); it is only
 * written because the visitor asked to join from the invite (strictly needed).
 * Fail-soft: blocked storage just means the visitor re-opens the link.
 */

const KEY = "resto_pending_invite";
const TOKEN_RE = /^[0-9a-f]{32}$/;
const MAX_AGE_MS = 60 * 60 * 1000;

export function isInviteToken(token: string | null | undefined): token is string {
  return typeof token === "string" && TOKEN_RE.test(token);
}

export function savePendingInvite(token: string, now = Date.now()): void {
  if (!isInviteToken(token)) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ token, at: now }));
  } catch {
    // Private mode / storage blocked.
  }
}

/** Reads and clears the pending token (one-shot). Null when absent or stale. */
export function takePendingInvite(now = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return null;
    window.localStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as { token?: unknown; at?: unknown };
    const token = typeof parsed.token === "string" ? parsed.token : null;
    const at = typeof parsed.at === "number" ? parsed.at : 0;
    if (!isInviteToken(token) || now - at > MAX_AGE_MS || now < at) return null;
    return token;
  } catch {
    return null;
  }
}
